import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import * as Actions from "us.collection.economy/actions";
import { RemitDetailsTable, CreditorRemitDetailsTable } from "./Components";
import { IRootState } from "us.collection/interfaces";
import { handleGoBack } from "us.helper";
import { getCreditorCount } from "./Functions";
import { DEFAULT_DRAWER_STATE } from "./Constants";
import { Guid } from "guid-typescript";

const {
  $PageHeader,
  $Divider,
  $Button,
  $Affix,
  $Drawer,
  $Skeleton,
  $AccountNoLabel,
} = Common.Components;

/**
 * @description - Remit details home view component navigate from remit history
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/2858189310/Remit+Proposal+UI+Design+and+Implementations
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 14/06/2024
 */
const RemitDetails: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { getRemittedDetails, remittedDetails, getRemitDetails } = props;
  const location = useLocation();
  const { journalId, accountNo, voucherNo } = (location.state || {}) as any;

  const remitMetaData = { journalId, accountNo, voucherNo };
  const [drawerState, setDrawerState] = useState(DEFAULT_DRAWER_STATE);

  useEffect(() => {
    if (journalId) {
      getRemittedDetails && getRemittedDetails(journalId);
    }
  }, []);

  const handleRemitDetailAction = (selectedRecord: any) => {
    getRemitDetails &&
      getRemitDetails({
        creditorNo: selectedRecord?.creditorNo,
        remitId: Guid.create().toString(),
        journalId: journalId,
      });
    setDrawerState({ visible: true, remitRecord: selectedRecord });
  };

  return (
    <>
      <div className="space-content">
        <$Affix offsetTop={48}>
          <div className="page-header header-border">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <$PageHeader
                  className="px-0"
                  onBack={() => handleGoBack(navigate)}
                  title={t(
                    "US.COLLECTION.ECONOMY:REMITTED_DETAILS.REMITTED_DETAILS"
                  )}
                />
                <$Divider className="bui-devider" type="vertical" />
                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="d-flex flex-column">
                      <div
                        data-testid="label-account-no"
                        className="font-weight-bold"
                      >
                        <$AccountNoLabel value={accountNo} />
                      </div>
                      <div className="text-muted">
                        {t("US.COLLECTION.ECONOMY:REMITTED_DETAILS.ACCOUNT_NO")}
                      </div>
                    </div>
                  </div>
                  <$Divider className="bui-devider mx-4" type="vertical" />
                  <div className="d-flex flex-column py-1">
                    <div>
                      <$Button
                        id="btnSelectCreditor"
                        type="link"
                        className="font-weight-bold m-0 p-0"
                        size="small"
                      >
                        {t(
                          "US.COLLECTION.ECONOMY:REMITTED_DETAILS.SELECTED_NUMBER_OF_CREDITORS"
                        )}
                      </$Button>
                    </div>
                    <div
                      data-testid="label-creditors"
                      className="font-weight-bold"
                      style={{ fontSize: "0.8rem" }}
                    >
                      {getCreditorCount(remittedDetails?.data)}
                    </div>
                  </div>
                  <$Divider className="bui-devider mx-4" type="vertical" />
                  <$Button
                    id={"btnRefresh"}
                    type="primary"
                    size="small"
                    className="ml-2"
                    loading={remittedDetails?.isLoading}
                    onClick={() =>
                      getRemittedDetails && getRemittedDetails(journalId)
                    }
                  >
                    {t("COMMON.REFRESH")}
                  </$Button>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <div className="d-flex flex-column mr-4">
                  <div
                    className="font-weight-bold"
                    data-testid="label-journal-id"
                  >
                    {journalId}
                  </div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMIT.JOURNAL_ID")}
                  </div>
                </div>
                <$Divider className="bui-devider" type="vertical" />

                <div className="d-flex flex-column ml-4">
                  <div
                    data-testid="label-voucher-no"
                    className="font-weight-bold"
                  >
                    {voucherNo}
                  </div>
                  <div className="text-muted">
                    {t("US.COLLECTION.ECONOMY:REMIT.VOUCHER_NO")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </$Affix>
        <div>
          <$Skeleton
            loading={remittedDetails?.isLoading}
            active
            paragraph={{ rows: 2 }}
          >
            <div>
              <RemitDetailsTable
                onCallAction={(selectedRecord: any) =>
                  handleRemitDetailAction(selectedRecord)
                }
              />
            </div>
          </$Skeleton>
        </div>
      </div>
      <$Drawer
        data-testid="drawer"
        title={t("US.COLLECTION.ECONOMY:REMIT.REMIT_DETAILS")}
        width={"95%"}
        open={drawerState.visible}
        onClose={() =>
          setDrawerState({ ...DEFAULT_DRAWER_STATE, visible: false })
        }
      >
        {drawerState.remitRecord && (
          /**
           * Remit details drawer content goes here
           */
          <CreditorRemitDetailsTable
            selectedRemitRecord={drawerState.remitRecord}
            remitMetaData={remitMetaData}
            handleNavigation={handleRemitDetailAction}
            onCancel={() =>
              setDrawerState({ ...DEFAULT_DRAWER_STATE, visible: false })
            }
          />
        )}
      </$Drawer>
    </>
  );
};

const mapStateToProps = (state: IRootState) => {
  const { remitDetails } = state;

  const { remittedDetails, remitBasicData } = remitDetails;
  return {
    remittedDetails,
    remitBasicData,
  };
};
const { postRemitDetails, remit } = Actions;
const { remitDetail } = postRemitDetails;
const { remitDetails } = remit;

const mapDispatchToProps = {
  getRemittedDetails: remitDetail.get,
  getRemitDetails: remitDetails.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RemitDetails);
